import { useNavigate, useParams, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback, useEffect } from "react"

import { GoodsReceiptProvider } from "../../../contexts"
import { useGetGoodsReceipt, useGoodsReceiptDetailsTabs, useGoodsReceiptStatusRedirect } from "../../../hooks"
import { useGetGoodsReceiptPallets } from "../../../hooks/useGetGoodsReceiptPallets"
import { useGoodsReceiptPalletActions } from "../../../hooks/useGoodsReceiptPalletActions"
import { useCheckedInGoodsReceiptActions } from "../hooks"

import { FinalizeCheckInSuccessDialog } from "./FinalizeCheckInSuccessDialog"

import { PermissionActionType } from "@/constants/permissions"
import { GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { BasicPathType, useAppView } from "@/hooks/useAppView"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import {
  GoodsReceiptDetailsLayout,
  GoodsReceiptFileFormDialog,
  GoodsReceiptPalletsCreateDialog,
} from "@/screens/WarehouseReceiving/components"
import { GoodReceiptActionType, GoodsReceiptPalletActionType } from "@/screens/WarehouseReceiving/types"

export const CheckedInGoodsReceiptDetailsLayout: FC = () => {
  const navigate = useNavigate()
  const { isTabletView, getBasicPathFor } = useAppView()
  const { goodsReceiptId } = useParams({
    from: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/checked-in/$goodsReceiptId/_layout`,
  })
  const { showSuccessDialog } = useSearch({
    from: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/checked-in/$goodsReceiptId/_layout`,
  })

  const { goodsReceipt, refetch: refetchGoodsReceipt } = useGetGoodsReceipt(goodsReceiptId)
  useGoodsReceiptStatusRedirect({ goodsReceipt, expectedStatus: GoodsReceiptStatus.CheckedIn })
  const { tabs, activeTab } = useGoodsReceiptDetailsTabs({
    goodsReceiptId,
    baseRoute: "checked-in",
  })
  const { hasPermissions } = usePermissionManager()
  const canMaintain = hasPermissions([PermissionActionType.WAREHOUSE_RECEIVING_MAINTENANCE])

  useEffect(() => {
    if (isTabletView) {
      window.scrollTo(0, 0)
    }
  }, [isTabletView])

  const goodsReceiptActions = useCheckedInGoodsReceiptActions({
    goodsReceipt,
    onRefetch: refetchGoodsReceipt,
  })

  const { refetch: refetchPallets } = useGetGoodsReceiptPallets(goodsReceiptId)

  const palletActions = useGoodsReceiptPalletActions({ onRefetch: () => void refetchPallets() })

  const handleSuccessDialogClose = useCallback(() => {
    navigate({
      to: `/${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/checked-in/$goodsReceiptId/purchase-orders`,
      params: { goodsReceiptId },
      search: { showSuccessDialog: undefined },
    })
  }, [navigate, getBasicPathFor, goodsReceiptId])

  if (!goodsReceipt) {
    return null
  }

  return (
    <>
      <GoodsReceiptProvider
        value={{
          goodsReceipt,
          onEdit: goodsReceiptActions.handleEditGoodsReceipt,
          onCreatePallets: canMaintain ? palletActions.handleCreate : undefined,
          onUpload: goodsReceiptActions.handleUpload,
        }}
      >
        <GoodsReceiptDetailsLayout
          goodsReceipt={goodsReceipt}
          tabs={tabs}
          activeTab={activeTab}
          backToPath={`${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/checked-in`}
          dataTestId='checked-in-goods-receipt-details'
          palletsCount={goodsReceipt.softReceiveData?.deliveredPalletsCount ?? 0}
        />

        {goodsReceiptActions.actionState?.type === GoodReceiptActionType.FileUpload && (
          <GoodsReceiptFileFormDialog
            onClose={goodsReceiptActions.handleDialogClose}
            onRefresh={goodsReceiptActions.handleFormDialogSubmit}
            goodsReceiptId={goodsReceiptId}
          />
        )}

        {palletActions.actionState?.type === GoodsReceiptPalletActionType.Create && (
          <GoodsReceiptPalletsCreateDialog
            goodsReceipt={goodsReceipt}
            onClose={palletActions.handleActionClose}
            onSubmit={palletActions.handleActionSuccess}
          />
        )}
      </GoodsReceiptProvider>

      {showSuccessDialog && (
        <FinalizeCheckInSuccessDialog goodsReceipt={goodsReceipt} onClose={handleSuccessDialogClose} />
      )}
    </>
  )
}
